import { DecimalPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FieldTree, FormField } from "@angular/forms/signals";
import { StrategyDspFunnel } from "@front/m19-api-client";
import { AuthService } from "@front/m19-services";
import {
  IDatepickerComponent,
  IDateRange,
  IErrorComponent,
  IFormFieldComponent,
  IInputGroupDirective,
  IInputGroupInput,
  IInputGroupSuffixDirective,
  ILabelComponent,
} from "@front/m19-ui";
import { DateUtils } from "@front/m19-utils";
import { TranslocoDirective } from "@jsverse/transloco";
import { DateTime } from "luxon";
import { map } from "rxjs";
import { DspFormService } from "@m19-board/dsp/dsp-create-strategy/dsp-form.service";
import { BudgetCurrencyBadgeComponent } from "@m19-board/shared/budget-currency-badge/budget-currency-badge.component";
import { MonthlyBudgetWarningComponent } from "@m19-board/shared/monthly-budget-warning/monthly-budget-warning.component";

export interface DspCreateStrategyBudgetModel {
  budget: number | null;
  budgetDates: IDateRange | null;
  monthlyBudget: number | null;
}

@Component({
  selector: "app-dsp-create-strategy-budget-step",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    FormField,
    DecimalPipe,
    IFormFieldComponent,
    IInputGroupDirective,
    IInputGroupInput,
    IInputGroupSuffixDirective,
    ILabelComponent,
    IErrorComponent,
    IDatepickerComponent,
    BudgetCurrencyBadgeComponent,
    MonthlyBudgetWarningComponent,
  ],
  templateUrl: "./dsp-create-strategy-budget-step.component.html",
})
export class DspCreateStrategyBudgetStepComponent {
  private readonly authService = inject(AuthService);
  private readonly dspFormService = inject(DspFormService);

  readonly budget = input.required<FieldTree<DspCreateStrategyBudgetModel>>();
  readonly funnel = input.required<StrategyDspFunnel | null>();
  readonly dealId = input<string | null>(null);

  readonly today = DateTime.utc();
  readonly nextYear = this.today.plus({ year: 1 });

  readonly isAwareness = computed(() => this.funnel() === StrategyDspFunnel.AWARENESS);

  readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });
  readonly dateFormat = computed(() => DateUtils.getDateFormatString(this.locale()));

  readonly currencyCode = toSignal(
    this.dspFormService.accountMarketplace$.pipe(map((am) => am.dspAdvertiser?.currency)),
  );

  private readonly videoDeals = toSignal(this.dspFormService.videoDeals$, { initialValue: [] });

  readonly maxImpressions = computed(() => {
    const amount = this.budget().budget().value();
    const dealId = this.dealId();
    const deal = dealId ? this.videoDeals().find((d) => d.dealId === dealId) : undefined;
    if (!amount || !deal?.price) return undefined;
    return Math.floor((amount / deal.price) * 1000);
  });

  selectBudgetDates(dates: IDateRange | null) {
    this.budget().budgetDates().markAsDirty();
    this.budget().budgetDates().value.set(dates);
  }
}
